import type {
  CubicBezierTuple,
  IEffectsPreviewStyle,
  IFilterItem,
  ILoopingAnimationItem,
  ILoopingKeyframeStop,
  IStylingEffects,
  ITransformItem,
  ITransitionItem,
  TransitionTimingFunction
} from "@kuika/kuika-cl-model";
import { StyleHelper } from "@kuika/kuika-cl-model";
import type { CSSProperties } from "react";
import { LOOPING_ANIMATION_DEFAULT_DURATION } from "../looping-animation-helpers";
import type {
  EffectPreset,
  EffectPresetFilterInput,
  EffectPresetInitialStateHint,
  EffectPresetPreviewType,
  EffectPresetTargetState,
  EffectPresetTransformInput,
  EffectPresetTransitionInput
} from "./effects-presets-types";

/**
 * Payload-driven preset preview model. The preset card no longer plays a
 * hand-authored ambient demo (the old `previewFx` library) — it derives the
 * preview from the preset's REAL payload via the same StyleHelper builders the
 * canvas uses, so what the card shows is what the component will do:
 *
 * - hover / focused presets  → CSS vars + `.card:hover` trigger rule
 * - pressed presets          → CSS vars + `.card:active` trigger rule
 * - base / disabled statics  → resting style applied inline
 * - motion / reveal presets  → WAAPI loop built from `animations[]` keyframe
 *   stops or from the reveal `initialStateHint`
 *
 * Pure module (no React component) shared with Vitest pure tests.
 */

export type PreviewSampleKind = "button" | "card" | "image" | "text" | "dot";
export type PreviewTriggerKind = "hover" | "active" | "loop" | "static";
export type PreviewBadgeTone = "hover" | "press" | "focus" | "always" | "load" | "disabled" | "base";
export type PreviewLoopSource = "animations" | "reveal";

export interface IPreviewSampleSpec {
  sampleKind: PreviewSampleKind;
  /** Text rendered inside the sample. Empty for image / dot samples. */
  label: string;
  triggerKind: PreviewTriggerKind;
  /** `data-trigger` attribute for the SCSS trigger rules (interaction presets only). */
  dataTrigger?: "hover" | "active";
  /** Uppercase trigger badge shown on the stage ("HOVER", "PRESS", ...). */
  badge: string;
  badgeTone: PreviewBadgeTone;
  /** Which payload field drives the WAAPI loop (loop presets only). */
  loopSource?: PreviewLoopSource;
}

/**
 * React inline style that also carries `--*` custom properties (the SCSS
 * trigger rules read them via `var()`).
 */
export type CssVarStyle = CSSProperties & Record<`--${string}`, string | number>;

/** Opacity floor for static previews of "hide" presets — a 0-opacity sample
 * would render an empty stage; a faint ghost communicates "hidden" honestly. */
const STATIC_PREVIEW_MIN_OPACITY: number = 0.12;

/**
 * CSS perspective is in px and therefore size-relative: a 900px perspective
 * that visibly tilts a ~350px component is imperceptible on a ~90px preview
 * sample. Scale it to the miniature (¼, floored) so the SAME angular tilt
 * reads the same — the rotation values themselves are never altered.
 */
const PREVIEW_PERSPECTIVE_SCALE: number = 0.25;
const PREVIEW_PERSPECTIVE_MIN: number = 150;

/** Reveal replay loop: enter, hold at rest, return to the hidden offset. */
export const REVEAL_PREVIEW_OPTIONS: KeyframeAnimationOptions = {
  duration: 2600,
  iterations: Infinity,
  easing: "ease-out"
};

const sampleKindFor = (previewType: EffectPresetPreviewType): PreviewSampleKind => {
  if (previewType === "card" || previewType === "reveal" || previewType === "threeD") {
    return "card";
  }
  if (previewType === "image") {
    return "image";
  }
  if (previewType === "text") {
    return "text";
  }
  if (previewType === "glow") {
    return "dot";
  }
  // hover | outline | utility | fade
  return "button";
};

const buttonLabelFor = (targetState: EffectPresetTargetState): string => {
  if (targetState === "pressed") {
    return "Press me";
  }
  if (targetState === "hover") {
    return "Hover me";
  }
  if (targetState === "focused") {
    return "Focus";
  }
  if (targetState === "disabled") {
    return "Disabled";
  }
  return "Button";
};

const labelFor = (sampleKind: PreviewSampleKind, preset: EffectPreset): string => {
  if (sampleKind === "button") {
    return buttonLabelFor(preset.targetState);
  }
  if (sampleKind === "card") {
    if (preset.previewType === "reveal" || preset.initialStateHint) {
      return "Reveal";
    }
    return "Card";
  }
  if (sampleKind === "text") {
    return "Label";
  }
  return "";
};

/**
 * Derive the sample shape, label, trigger and badge for a preset card.
 * `previewType` picks the shape; `targetState` (+ payload animations / reveal
 * hint) picks the trigger and the badge that teaches the user WHEN the effect
 * fires.
 */
export const getPreviewSampleSpec = (preset: EffectPreset): IPreviewSampleSpec => {
  const sampleKind: PreviewSampleKind = sampleKindFor(preset.previewType);
  const label: string = labelFor(sampleKind, preset);
  if (preset.targetState === "hover") {
    return { sampleKind, label, triggerKind: "hover", dataTrigger: "hover", badge: "HOVER", badgeTone: "hover" };
  }
  if (preset.targetState === "pressed") {
    return { sampleKind, label, triggerKind: "active", dataTrigger: "active", badge: "PRESS", badgeTone: "press" };
  }
  if (preset.targetState === "focused") {
    // Hover-proxy: a non-focusable preview tile can't receive real :focus, so
    // the focus ring is demonstrated on hover instead (badge still says FOCUS).
    return { sampleKind, label, triggerKind: "hover", dataTrigger: "hover", badge: "FOCUS", badgeTone: "focus" };
  }
  if (preset.targetState === "disabled") {
    return { sampleKind, label, triggerKind: "static", badge: "DISABLED", badgeTone: "disabled" };
  }
  const animationCount: number = preset.payload.animations?.length ?? 0;
  if (animationCount > 0) {
    const firstAnimation: ILoopingAnimationItem = preset.payload.animations![0];
    // Finite repeats ("attention" effects) advertise their count instead of
    // pretending to run forever.
    if (firstAnimation.infinite === false) {
      const iterationCount: number = firstAnimation.iterationCount ?? 1;
      return {
        sampleKind,
        label,
        triggerKind: "loop",
        badge: `${iterationCount}×`,
        badgeTone: "always",
        loopSource: "animations"
      };
    }
    return { sampleKind, label, triggerKind: "loop", badge: "ALWAYS", badgeTone: "always", loopSource: "animations" };
  }
  if (preset.initialStateHint) {
    return { sampleKind, label, triggerKind: "loop", badge: "ON LOAD", badgeTone: "load", loopSource: "reveal" };
  }
  return { sampleKind, label, triggerKind: "static", badge: "BASE", badgeTone: "base" };
};

/**
 * Project a preset payload into a minimal IStylingEffects so the shared
 * StyleHelper builders can emit the preview CSS. Item ids are preview-local
 * placeholders (never persisted). Cursor / pointer-events / mix-blend-mode and
 * backdropFilters are intentionally not previewed (no meaningful effect on a
 * small tile); animations / entryAnimation are driven separately via WAAPI.
 */
export const buildPreviewEffects = (preset: EffectPreset): IStylingEffects => {
  const effects: IStylingEffects = { effectsId: null, kuikaid: "preset-preview" };
  if (preset.payload.basic?.opacity !== undefined) {
    effects.opacity = preset.payload.basic.opacity;
  }
  if (preset.payload.outline) {
    effects.outlineStyle = preset.payload.outline.outlineStyle;
    effects.outlineWidth = preset.payload.outline.outlineWidth;
    effects.outlineOffset = preset.payload.outline.outlineOffset;
    effects.outlineColor = preset.payload.outline.outlineColor;
  }
  if (preset.payload.transformSettings?.perspective !== undefined) {
    effects.perspective = Math.max(
      PREVIEW_PERSPECTIVE_MIN,
      preset.payload.transformSettings.perspective * PREVIEW_PERSPECTIVE_SCALE
    );
  }
  if (preset.payload.transforms) {
    effects.transforms = preset.payload.transforms.map(
      (item: EffectPresetTransformInput, index: number): ITransformItem => ({ ...item, id: `pv-t-${index}` })
    );
  }
  if (preset.payload.filters) {
    effects.filters = preset.payload.filters.map((item: EffectPresetFilterInput, index: number): IFilterItem => ({
      ...item,
      id: `pv-f-${index}`
    }));
  }
  if (preset.payload.transitions) {
    effects.transitions = preset.payload.transitions.map(
      (item: EffectPresetTransitionInput, index: number): ITransitionItem => ({ ...item, id: `pv-tr-${index}` })
    );
  }
  return effects;
};

/**
 * Inline style for the sample element. Interaction presets publish the target
 * values as `--fx-*` custom properties (consumed by the SCSS
 * `.card:hover/:active` trigger rules) so the sample rests in its neutral
 * state; static presets apply the resting look directly. Loop presets get no
 * inline values — WAAPI drives them.
 */
export const buildSampleStyle = (previewStyle: IEffectsPreviewStyle, triggerKind: PreviewTriggerKind): CssVarStyle => {
  const style: CssVarStyle = {};
  if (triggerKind === "hover" || triggerKind === "active") {
    if (previewStyle.opacity !== undefined) {
      style["--fx-opacity"] = previewStyle.opacity;
    }
    if (previewStyle.transform) {
      style["--fx-transform"] = previewStyle.transform;
    }
    if (previewStyle.filter) {
      style["--fx-filter"] = previewStyle.filter;
    }
    if (previewStyle.outline) {
      style["--fx-outline"] = previewStyle.outline;
    }
    if (previewStyle.outlineOffset) {
      style["--fx-outline-offset"] = previewStyle.outlineOffset;
    }
    if (previewStyle.transition) {
      style["--fx-transition"] = previewStyle.transition;
    }
    return style;
  }
  if (triggerKind === "static") {
    if (previewStyle.opacity !== undefined) {
      style.opacity = Math.max(STATIC_PREVIEW_MIN_OPACITY, previewStyle.opacity);
    }
    if (previewStyle.transform) {
      style.transform = previewStyle.transform;
    }
    if (previewStyle.filter) {
      style.filter = previewStyle.filter;
    }
    if (previewStyle.outline) {
      style.outline = previewStyle.outline;
    }
    if (previewStyle.outlineOffset) {
      style.outlineOffset = previewStyle.outlineOffset;
    }
    return style;
  }
  return style;
};

const easingFor = (
  timingFunction: TransitionTimingFunction | undefined,
  cubicBezier: CubicBezierTuple | undefined
): string => {
  if (timingFunction === "cubic-bezier" && cubicBezier && cubicBezier.length === 4) {
    return `cubic-bezier(${cubicBezier[0]},${cubicBezier[1]},${cubicBezier[2]},${cubicBezier[3]})`;
  }
  return timingFunction ?? "ease";
};

/**
 * WAAPI keyframes from a looping animation's stops — the exact timeline the
 * generated `@keyframes` rule would contain, built with the same StyleHelper
 * builders. Only properties present on a stop are emitted (WAAPI interpolates
 * the rest from the underlying value).
 */
export const buildLoopKeyframes = (item: ILoopingAnimationItem): Keyframe[] => {
  const stops: ILoopingKeyframeStop[] = [...(item.keyframes ?? [])].sort(
    (a: ILoopingKeyframeStop, b: ILoopingKeyframeStop): number => a.offset - b.offset
  );
  return stops.map((stop: ILoopingKeyframeStop): Keyframe => {
    const stopStyle: IEffectsPreviewStyle = StyleHelper.buildEffectsPreviewStyle({
      effectsId: null,
      kuikaid: "preset-preview",
      transforms: stop.transforms,
      filters: stop.filters,
      opacity: stop.opacity
    });
    const keyframe: Keyframe = { offset: Math.min(1, Math.max(0, stop.offset / 100)) };
    if (stopStyle.transform) {
      keyframe.transform = stopStyle.transform;
    }
    if (stopStyle.filter) {
      keyframe.filter = stopStyle.filter;
    }
    if (stopStyle.opacity !== undefined) {
      keyframe.opacity = stopStyle.opacity;
    }
    return keyframe;
  });
};

export const buildLoopAnimationOptions = (item: ILoopingAnimationItem): KeyframeAnimationOptions => {
  let iterations: number;
  if (item.infinite === false) {
    iterations = item.iterationCount ?? 1;
  } else {
    iterations = Infinity;
  }
  return {
    duration: item.durationMs ?? LOOPING_ANIMATION_DEFAULT_DURATION,
    delay: item.delayMs ?? 0,
    easing: easingFor(item.timingFunction, item.cubicBezier),
    direction: item.direction ?? "normal",
    iterations
  };
};

/**
 * Looping replay of a reveal preset's mount animation: start at the hidden
 * `initialStateHint` offset, settle to rest, hold, then return so the loop
 * re-enters cleanly. Pairs with {@link REVEAL_PREVIEW_OPTIONS}. An optional
 * `enterEasing` (the preset's own `entryTiming.timingFunction`, e.g. an
 * overshoot bezier) shapes the enter segment so character reveals preview with
 * their character.
 */
export const buildRevealKeyframes = (hint: EffectPresetInitialStateHint, enterEasing?: string): Keyframe[] => {
  const transforms: ITransformItem[] = [];
  if (hint.translateX !== undefined || hint.translateY !== undefined) {
    transforms.push({ id: "pv-rv-move", type: "move", x: hint.translateX ?? 0, y: hint.translateY ?? 0, unit: "px" });
  }
  if (hint.scale !== undefined) {
    transforms.push({ id: "pv-rv-scale", type: "scale", x: hint.scale, y: hint.scale, unit: "none" });
  }
  if (hint.rotateZ !== undefined) {
    transforms.push({ id: "pv-rv-rotate", type: "rotate", z: hint.rotateZ, unit: "deg" });
  }
  const fromStyle: IEffectsPreviewStyle = StyleHelper.buildEffectsPreviewStyle({
    effectsId: null,
    kuikaid: "preset-preview",
    transforms,
    opacity: hint.opacity
  });
  const fromTransform: string = fromStyle.transform ?? "none";
  const fromOpacity: number = fromStyle.opacity ?? 1;
  const enter: Keyframe = { offset: 0, transform: fromTransform, opacity: fromOpacity };
  if (enterEasing) {
    // Per-keyframe easing shapes only the 0 → 30% enter segment.
    enter.easing = enterEasing;
  }
  return [
    enter,
    { offset: 0.3, transform: "none", opacity: 1 },
    { offset: 0.85, transform: "none", opacity: 1 },
    { offset: 1, transform: fromTransform, opacity: fromOpacity }
  ];
};
